import React from 'react';
import { TextStyle, StyleProp } from 'react-native';

import { Text, TextProps } from '@/components/ui/text';
import { formatBusinessDate } from '@/utils/date-utils';

interface DateDisplayProps {
	date: Date | number | undefined | null;
	dateOnly?: boolean;
	/**
	 * The value is a business date (midnight-UTC ms day marker, e.g.
	 * boardMeetingDate) — render its UTC calendar day. Implies date-only
	 * output, so `dateOnly` is redundant alongside it. Without this, local
	 * rendering shows the previous day west of Greenwich. Leave unset for real
	 * moments (createdAt etc.), which should reflect local time.
	 */
	businessDate?: boolean;
	style?: StyleProp<TextStyle>;
	fallback?: string;
	variant?: TextProps['variant'];
	ageColors?: {
		[key: number]: string;
	};
	prefix?: string;
}

export const DateDisplay: React.FC<DateDisplayProps> = ({
	date,
	dateOnly = false,
	businessDate = false,
	style,
	fallback = '-',
	variant,
	ageColors,
	prefix,
}) => {
	if (!date) {
		return <Text
			variant={variant}
			style={[{ fontSize: 16}, style]}>{fallback}</Text>;
	}

	const dateObj = date instanceof Date ? date : new Date(date);
	const formattedDate = businessDate
		? formatBusinessDate(dateObj.getTime())
		: dateOnly
			? dateObj.toLocaleDateString()
			: dateObj.toLocaleString();

	const ageInDays = Math.floor((new Date().getTime() - dateObj.getTime()) / (1000 * 60 * 60 * 24));

	let ageColor: string | undefined;

	if (ageColors && ageInDays > 0) {
		for (const [age, color] of Object.entries(ageColors)) {
			if (ageInDays >= Number(age)) {
				ageColor = color;
			}
		}
	}


	return (
		<Text variant={variant} style={[
			style,
			ageColor && { color: ageColor, fontWeight: '700' },
		]}>
			{prefix && <Text variant={variant} style={[
				style,
				{ marginRight: 6 },
			]}>{prefix}</Text>}
			{formattedDate}
		</Text>
	);
};
